// Datos clínicos del consultorio → API / PostgreSQL. Sin localStorage (solo cookies JWT).

import {
  fetchPatients,
  savePatientApi,
  fetchAppointments,
  saveAppointmentApi,
  fetchRecords,
  saveRecordApi,
  deleteRecordApi,
  migrateFullLocal,
  fetchClientState,
  saveClientState,
  RECORD_TYPES,
} from '../api/consultorioClinical';
import { getToken, COOKIE_KEYS } from '../utils/cookies';
import {
  purgeConsultorioLocalStorage,
  purgeConsultorioFlagsOnly,
} from '../utils/consultorioLocalStorage';

function hasConsultorioAuth() {
  return !!getToken(COOKIE_KEYS.AUTH_TOKEN);
}

function isAuthError(err) {
  return err?.response?.status === 401;
}

const KEY_PACIENTES = 'app_pacientes';
const KEY_CITAS = 'app_citas';
const KEY_DENTAL_CHARTS = 'app_dental_charts';
const KEY_RADIOGRAFIAS = 'app_radiografias';
const KEY_RECETAS = 'app_recetas';
const KEY_PAGOS = 'app_pagos_consultorio';
const KEY_PRODUCTOS_CONSULTORIO = 'app_productos_consultorio';
const KEY_FACTURAS_CONSULTORIO = 'app_facturas_consultorio';
const KEY_SESIONES_TRATAMIENTO = 'app_sesiones_tratamiento';
const KEY_FICHA_MED_CATALOGOS = 'app_ficha_medica_catalogos';

/** Claves válidas del catálogo por doctor (historia médica + historia odontológica MSP) */
export const FICHA_MED_CATALOG_KEYS = [
  'alergiasTiposExtras',
  'viaAdministracionExtras',
  'enfermedadesCronicasExtras',
  'habitosExtras',
  'antecedentesOdontoExtras',
  'examenEstomatognaticoExtras',
  'signosVitalesOpcionesExtras',
  'odontoHigieneExtras'
];

const catalogDefaults = () => ({
  alergiasTiposExtras: [],
  viaAdministracionExtras: [],
  enfermedadesCronicasExtras: [],
  habitosExtras: [],
  antecedentesOdontoExtras: [],
  examenEstomatognaticoExtras: [],
  signosVitalesOpcionesExtras: [],
  odontoHigieneExtras: [],
});

const catalogCache = {};

export function getFichaMedicaCatalogosExtrasSync(idDoctor) {
  if (idDoctor == null || idDoctor === '') return catalogDefaults();
  return catalogCache[String(idDoctor)] || catalogDefaults();
}

export const getFichaMedicaCatalogosExtras = async (idDoctor) => {
  if (idDoctor == null || idDoctor === '') return catalogDefaults();
  await migrateAllLocalStorageToServer();
  try {
    const rows = await fetchRecords(RECORD_TYPES.DOCTOR_CATALOG, { doctorIds: [idDoctor] });
    const ext = `doctor-catalog-${idDoctor}`;
    const ent = rows.find((r) => r.id === ext) || rows[0] || {};
    const out = catalogDefaults();
    FICHA_MED_CATALOG_KEYS.forEach((k) => {
      out[k] = Array.isArray(ent[k]) ? ent[k] : [];
    });
    catalogCache[String(idDoctor)] = out;
    return out;
  } catch (e) {
    console.warn('getFichaMedicaCatalogosExtras:', e);
    return catalogDefaults();
  }
};

/** Añade una etiqueta propia del doctor a una lista (`FICHA_MED_CATALOG_KEYS`). Devuelve `{ id, label }` o null. */
export const addFichaMedicaCatalogoExtra = async (idDoctor, listaKey, label) => {
  if (!FICHA_MED_CATALOG_KEYS.includes(listaKey)) return null;
  const t = String(label || '').trim();
  if (!t || idDoctor == null || idDoctor === '') return null;
  const id = `ext-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const item = { id, label: t };
  const current = await getFichaMedicaCatalogosExtras(idDoctor);
  if (!Array.isArray(current[listaKey])) current[listaKey] = [];
  current[listaKey].push(item);
  await saveRecordApi(RECORD_TYPES.DOCTOR_CATALOG, `doctor-catalog-${idDoctor}`, {
    ...current,
    idDoctor,
  });
  return item;
};

export const removeFichaMedicaCatalogoExtra = async (idDoctor, listaKey, itemId) => {
  if (!FICHA_MED_CATALOG_KEYS.includes(listaKey)) return false;
  if (idDoctor == null || idDoctor === '') return false;
  const current = await getFichaMedicaCatalogosExtras(idDoctor);
  if (!Array.isArray(current[listaKey])) return false;
  current[listaKey] = current[listaKey].filter((x) => x.id !== itemId);
  await saveRecordApi(RECORD_TYPES.DOCTOR_CATALOG, `doctor-catalog-${idDoctor}`, {
    ...current,
    idDoctor,
  });
  return true;
};

function readLocalPacientes() {
  try {
    return JSON.parse(localStorage.getItem(KEY_PACIENTES) || '[]');
  } catch {
    return [];
  }
}

function readLocalCitas() {
  try {
    return JSON.parse(localStorage.getItem(KEY_CITAS) || '[]');
  } catch {
    return [];
  }
}

function collectLocalStoragePayload() {
  const periodontograms = [];
  const ui_prefs = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (!k) continue;
    if (k.startsWith('periodontograma_')) {
      try {
        periodontograms.push({
          id: `perio-${k.slice('periodontograma_'.length)}`,
          idPaciente: k.slice('periodontograma_'.length),
          datos: JSON.parse(localStorage.getItem(k) || '{}'),
        });
      } catch { /* skip */ }
    } else if (k.startsWith('pd_section_')) {
      ui_prefs.push({ key: k, value: localStorage.getItem(k) });
    }
  }
  let doctor_catalogs = {};
  try {
    doctor_catalogs = JSON.parse(localStorage.getItem(KEY_FICHA_MED_CATALOGOS) || '{}');
  } catch { /* noop */ }
  let consent_templates = {};
  try {
    consent_templates = JSON.parse(localStorage.getItem('app_consentimiento_plantilla_tenant_v2') || '{}');
  } catch { /* noop */ }
  return {
    patients: readLocalPacientes(),
    appointments: readLocalCitas(),
    dental_charts: JSON.parse(localStorage.getItem(KEY_DENTAL_CHARTS) || '[]'),
    radiographs: JSON.parse(localStorage.getItem(KEY_RADIOGRAFIAS) || '[]'),
    prescriptions: JSON.parse(localStorage.getItem(KEY_RECETAS) || '[]'),
    payments: JSON.parse(localStorage.getItem(KEY_PAGOS) || '[]'),
    treatment_sessions: JSON.parse(localStorage.getItem(KEY_SESIONES_TRATAMIENTO) || '[]'),
    products: JSON.parse(localStorage.getItem(KEY_PRODUCTOS_CONSULTORIO) || '[]'),
    clinic_invoices: JSON.parse(localStorage.getItem(KEY_FACTURAS_CONSULTORIO) || '[]'),
    doctor_catalogs,
    consent_templates,
    periodontograms,
    ui_prefs,
  };
}

let migrationResolved = false;
let migrationInFlight = null;

/** Migra una vez datos legacy del navegador → PostgreSQL; flag en servidor (client-state). */
export async function migrateAllLocalStorageToServer() {
  if (!hasConsultorioAuth()) return;
  if (migrationResolved) return;
  if (migrationInFlight) return migrationInFlight;

  migrationInFlight = (async () => {
  try {
    const state = await fetchClientState();
    if (state?.migrated_full) {
      purgeConsultorioFlagsOnly();
      migrationResolved = true;
      return;
    }
  } catch (e) {
    if (isAuthError(e)) return;
  }

  const payload = collectLocalStoragePayload();
  const hasData =
    payload.patients.length > 0
    || payload.appointments.length > 0
    || payload.dental_charts.length > 0
    || payload.radiographs.length > 0
    || payload.prescriptions.length > 0
    || payload.payments.length > 0
    || payload.treatment_sessions.length > 0
    || payload.products.length > 0
    || payload.clinic_invoices.length > 0
    || payload.periodontograms.length > 0
    || payload.ui_prefs.length > 0
    || Object.keys(payload.doctor_catalogs || {}).length > 0
    || Object.keys(payload.consent_templates || {}).length > 0;

  try {
    if (hasData) {
      await migrateFullLocal(payload);
    }
    await saveClientState({
      migrated_full: true,
      migrated_at: new Date().toISOString(),
    });
  } catch (e) {
    if (!isAuthError(e)) console.warn('Migración completa a API:', e);
    return;
  }
  purgeConsultorioLocalStorage();
  migrationResolved = true;
  })().finally(() => {
    migrationInFlight = null;
  });

  return migrationInFlight;
}

export { purgeConsultorioLocalStorage, purgeConsultorioFlagsOnly };

export const migrateClinicalFromLocalStorage = migrateAllLocalStorageToServer;

export const getPacientes = async (idDoctor) => {
  if (!hasConsultorioAuth()) return [];
  await migrateAllLocalStorageToServer();
  try {
    const list = await fetchPatients([idDoctor]);
    return list.sort((a, b) => (b.fechaRegistro || '').localeCompare(a.fechaRegistro || ''));
  } catch (e) {
    if (isAuthError(e)) return [];
    throw e;
  }
};

/** Valores por defecto para la ficha odontológica (todos los campos del registro) */
export const FICHA_ODONTO_DEFAULTS = {
  // --- DATOS PERSONALES ---
  nombre: '',
  apellidos: '',
  cedula: '',
  email: '',
  telefono: '',
  fechaNacimiento: '',
  edad: '',
  rangoEdad: '',
  sexo: '',
  embarazada: '',
  direccion: '',
  ocupacion: '',
  estadoCivil: '',
  establecimiento: '',
  numeroHistoriaClinica: '',

  /** Cédula, Pasaporte o Ruc — el número se guarda en `cedula` */
  tipoIdentificacion: '',

  // --- UBICACIÓN ---
  direccionProvincia: '',
  direccionCanton: '',
  direccionParroquia: '',
  direccionCiudad: '',
  direccionExacta: '',
  
  // --- CONTACTO DE EMERGENCIA ---
  contactoEmergencia: '',
  telefonoEmergencia: '',
  contactoEmergenciaRelacion: '',
  /** Lista de contactos: { nombre, telefono, relacion } */
  contactosEmergencia: [],
  
  // --- SEGURO ---
  seguro: '',
  numeroSeguro: '',
  tipoSeguro: '',
  
  // --- 1. MOTIVO DE CONSULTA ---
  motivoConsulta: '',
  
  // --- 2. ENFERMEDAD O PROBLEMA ACTUAL ---
  enfermedadActual: '',
  sintomas: '',
  cronologia: '',
  localizacion: '',
  caracteristicas: '',
  intensidad: '',
  causaAparente: '',
  sintomasAsociados: '',
  evolucion: '',
  estadoActual: '',
  
  // --- 3. ANTECEDENTES PERSONALES Y FAMILIARES ---
  alergiaAntibiotico: '',
  alergiaAnestesia: '',
  hemorragias: '',
  vih: '',
  tuberculosis: '',
  asma: '',
  diabetes: '',
  hipertension: '',
  antecedentesCancer: '',
  antecedentesCardiopatia: '',
  frecuenciaCardiaca: '',
  otrosAntecedentes: '',
  alergias: '',
  /** Categorías de alergias seleccionadas: medicamentos, alimentos, latex, otros */
  alergiasTipos: [],
  alergiasEspecificacion: '',
  medicamentosActuales: '',
  medicamentosActualesFilas: [],
  enfermedadesCronicas: '',
  enfermedadesCronicasSeleccion: [],
  enfermedadesCronicasOtro: '',
  cirugiasPrevias: '',
  habitosFumar: '',
  habitosAlcohol: '',
  habitosDrogas: '',
  habitosSeleccion: [],
  embarazo: '',
  embarazoTrimestre: '',
  vacunaciones: '',
  grupoSanguineo: '',
  /** Valores para filas dinámicas de antecedentes (Historia odonto), id ext-* → 'Sí'|'No'|'' */
  antecedentesOdontoExtrasValores: {},
  /** Detalle por ítem cuando refiere «Sí» (clave de campo o id de fila extra) */
  antecedentesOdontoDetalles: {},
  
  // --- 4. SIGNOS VITALES ---
  presionArterial: '',
  frecuenciaCardiacaSignos: '',
  temperatura: '',
  frecuenciaRespiratoria: '',
  saturacionOxigeno: '',
  signosVitalesExtrasValores: {},
  
  // --- 5. EXAMEN DEL SISTEMA ESTOMATOGNÁTICO ---
  examenLabios: '',
  examenMejillas: '',
  examenMaxilarSuperior: '',
  examenMaxilarInferior: '',
  examenLengua: '',
  examenPaladar: '',
  examenPiso: '',
  examenCarrillos: '',
  examenGlandulasSalivales: '',
  examenOrofaringe: '',
  examenATM: '',
  examenGanglios: '',
  /** Región con hallazgo marcada (clave de campo o id extra) */
  examenEstomatognaticoMarcas: {},
  /** Anomalía o descripción por región marcada */
  examenEstomatognaticoDetalles: {},
  examenOdontoExtrasValores: {},
  examenOdontoExtrasMarcas: {},
  
  // --- 7. INDICADORES DE SALUD BUCAL ---
  // Higiene Oral Simplificada (tabla MSP 6 filas)
  higieneOralSimplificada: null,
  placa: '',
  calculo: '',
  // Enfermedad Periodontal
  gingivitis: '',
  enfermedadPeriodontal: '',
  // Maloclusión
  maloclusion: '',
  angleClasificacion: '',
  // Fluorosis
  fluorosis: '',
  
  // --- 8. ÍNDICE CPO-ceo ---
  indiceCPO_D: '',
  indiceCPO_C: '',
  indiceCPO_P: '',
  indiceCPO_O: '',
  indiceCPO_Total: '',
  indiceCeo_d: '',
  indiceCeo_c: '',
  indiceCeo_e: '',
  indiceCeo_o: '',
  indiceCeo_Total: '',
  
  // --- 10. PLANES DE DIAGNÓSTICO ---
  biometria: '',
  quimicaSanguinea: '',
  rayosX: '',
  otrosExamenes: '',
  
  // --- 11. DIAGNÓSTICO ---
  diagnosticoPresuntivo1: '',
  diagnosticoPresuntivoCIE10_1: '',
  diagnosticoDefinitivo1: '',
  diagnosticoDefinitivoCIE10_1: '',
  diagnosticoPresuntivo2: '',
  diagnosticoPresuntivoCIE10_2: '',
  diagnosticoDefinitivo2: '',
  diagnosticoDefinitivoCIE10_2: '',
  diagnosticoPresuntivo3: '',
  diagnosticoPresuntivoCIE10_3: '',
  diagnosticoDefinitivo3: '',
  diagnosticoDefinitivoCIE10_3: '',
  diagnosticoPresuntivo4: '',
  diagnosticoPresuntivoCIE10_4: '',
  diagnosticoDefinitivo4: '',
  diagnosticoDefinitivoCIE10_4: '',
  
  // --- CAMPOS EXISTENTES (COMPATIBILIDAD) ---
  fechaUltimaVisitaOdontologica: '',
  tratamientosPrevios: '',
  higieneBucal: '',
  dolorSintomasActuales: '',
  intensidadDolor: '',
  enfermedadesBucalesCronicas: '',
  protesisAparatos: '',
  consentimientoInformado: false,
  consentimientoInformadoFecha: '',
  /** Texto legal personalizado del paciente (vacío = plantilla del consultorio) */
  consentimientoInformadoTexto: '',
  /** Firma del paciente: data URL (archivos pequeños) */
  consentimientoInformadoFirmaDataUrl: '',
  /** Firma en IndexedDB (PDF/imágenes grandes) */
  consentimientoInformadoFirmaStorageId: '',
  /** Origen: dibujo | archivo | archivo_pdf */
  consentimientoInformadoFirmaModo: '',
  notas: '',
  historialMedico: '',
  fechaRegistro: '',
  fechaApertura: '',
  fechaControl: '',
  /** Plan combinado MSP (diagnóstico · terapéutico · educativo) complementario al odontograma */
  planDiagTerapeuticoEducativoMSP: '',
  /** Texto único tratamiento/actuaciones clínicas asociadas al mismo registro clínico */
  tratamientoMSPTextoResumen: '',
  /** Profesional responsable MSP (firmas ampliadas en consultorio cuando aplique) */
  profesionalNombreApellidos: '',
  profesionalRegistroMSP: '',
  profesionalFirmaElectronicaNotas: '',

  /** Tablas MSP (documento ítems software / historia clínica) */
  tablaDiagnosticosMSP: [], // { diagnostico, cie10, tipo: 'presuntivo'|'definitivo' }
  tablaTratamientosMSP: [] // { fecha, diagnostico, tratamiento, prescripciones, codigoFirma }
};

const CONTACTO_EMERG_BLANK = { nombre: '', telefono: '', relacion: '' };

function rowTieneContactoEmergencia(r) {
  if (!r) return false;
  return !!(
    (r.nombre || r.contactoEmergencia || '').toString().trim() ||
    (r.telefono || r.telefonoEmergencia || '').toString().trim() ||
    (r.relacion || r.contactoEmergenciaRelacion || '').toString().trim()
  );
}

/** Normaliza filas de contactos de emergencia (sin trim: permite espacios mientras se escribe). */
export function sanitizeContactosEmergencia(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [{ ...CONTACTO_EMERG_BLANK }];
  return rows.map((r) => ({
    nombre: (r?.nombre ?? r?.contactoEmergencia ?? '').toString(),
    telefono: (r?.telefono ?? r?.telefonoEmergencia ?? '').toString(),
    relacion: (r?.relacion ?? r?.contactoEmergenciaRelacion ?? '').toString(),
  }));
}

function trimContactoEmergenciaRow(r) {
  return {
    nombre: (r.nombre || '').trim(),
    telefono: (r.telefono || '').trim(),
    relacion: (r.relacion || '').trim(),
  };
}

/** Filas con datos + una fila vacía al final para seguir agregando. */
export function compactContactosEmergenciaRows(rows) {
  const filled = sanitizeContactosEmergencia(rows).filter(rowTieneContactoEmergencia);
  return [...filled, { ...CONTACTO_EMERG_BLANK }];
}

/** Persiste todos los contactos con datos y sincroniza el primer contacto en campos legacy. */
export function prepareContactosEmergenciaForStorage(paciente) {
  let rows = sanitizeContactosEmergencia(paciente?.contactosEmergencia)
    .map(trimContactoEmergenciaRow)
    .filter(rowTieneContactoEmergencia);
  if (!rows.length) {
    const legacy = {
      nombre: (paciente?.contactoEmergencia || '').toString().trim(),
      telefono: (paciente?.telefonoEmergencia || '').toString().trim(),
      relacion: (paciente?.contactoEmergenciaRelacion || '').toString().trim(),
    };
    if (rowTieneContactoEmergencia(legacy)) rows = [legacy];
  }
  const first = rows[0] || { ...CONTACTO_EMERG_BLANK };
  return {
    contactosEmergencia: rows,
    contactoEmergencia: first.nombre || '',
    telefonoEmergencia: first.telefono || '',
    contactoEmergenciaRelacion: first.relacion || '',
  };
}

export const savePaciente = async (paciente, idDoctor) => {
  const toSave = { ...paciente, idDoctor, ...prepareContactosEmergenciaForStorage(paciente) };
  if (!toSave.id) {
    toSave.id = `pac-${Date.now()}`;
    toSave.fechaRegistro = new Date().toISOString();
  }
  return savePatientApi(toSave.id, toSave);
};

export const getCitas = async (idDoctor) => {
  if (!hasConsultorioAuth()) return [];
  await migrateAllLocalStorageToServer();
  try {
    const list = await fetchAppointments([idDoctor]);
    return list.sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
  } catch (e) {
    if (isAuthError(e)) return [];
    throw e;
  }
};

/** Citas de varios profesionales (mismo consultorio). ids = customer_id de cada doctor. */
export const getCitasConsultorio = async (idsDoctores) => {
  if (!hasConsultorioAuth()) return [];
  await migrateAllLocalStorageToServer();
  const ids = (idsDoctores || []).filter((x) => x != null && x !== '');
  try {
    const list = await fetchAppointments(ids.length ? ids : []);
    return list.sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
  } catch (e) {
    if (isAuthError(e)) return [];
    throw e;
  }
};

/** Pacientes registrados por cualquiera de los doctores indicados. */
export const getPacientesConsultorio = async (idsDoctores) => {
  if (!hasConsultorioAuth()) return [];
  await migrateAllLocalStorageToServer();
  const ids = (idsDoctores || []).filter((x) => x != null && x !== '');
  try {
    return await fetchPatients(ids.length ? ids : []);
  } catch (e) {
    if (isAuthError(e)) return [];
    throw e;
  }
};

export const saveCita = async (cita, idDoctor) => {
  const c = { ...cita, idDoctor };
  if (!c.id) c.id = `cita-${Date.now()}`;
  return saveAppointmentApi(c.id, c);
};

export const getDentalCharts = async (idPaciente) => {
  await migrateAllLocalStorageToServer();
  const list = await fetchRecords(RECORD_TYPES.DENTAL_CHART, { patientId: idPaciente });
  return list
    .filter((c) => String(c.idPaciente) === String(idPaciente))
    .sort((a, b) => new Date(b.fecha || b.updated_at) - new Date(a.fecha || a.updated_at));
};

export const saveDentalChart = async (chart) => {
  const c = { ...chart };
  if (!c.id) c.id = `chart-${Date.now()}`;
  return saveRecordApi(RECORD_TYPES.DENTAL_CHART, c.id, c);
};

/* ── Configuración de dientes activos por paciente ──────────────────────────
 *
 * Estructura: { dientesActivos: { "11": true, "18": false, ... }, fecha: ISO }
 * Si un número FDI NO aparece en el objeto → se considera activo (true).
 * false = pieza ausente / bloqueada.
 * ─────────────────────────────────────────────────────────────────────────── */
/**
 * Caché en memoria solo durante la sesión (persistencia en patient_dental_config vía odontogramConfig).
 */
export const getDienteConfig = async () => ({ dientesActivos: {} });

export const saveDienteConfig = async (_idPaciente, _idDoctor, dientesActivos) => ({
  dientesActivos,
  fecha: new Date().toISOString(),
});

// Numeración FDI permanente (32 dientes adultos)
export const FDI_UPPER_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11];
export const FDI_UPPER_LEFT = [21, 22, 23, 24, 25, 26, 27, 28];
export const FDI_LOWER_LEFT = [31, 32, 33, 34, 35, 36, 37, 38];
export const FDI_LOWER_RIGHT = [41, 42, 43, 44, 45, 46, 47, 48];
export const FDI_ALL = [...FDI_UPPER_RIGHT, ...FDI_UPPER_LEFT, ...FDI_LOWER_LEFT, ...FDI_LOWER_RIGHT];

// Numeración FDI temporal (20 dientes)
export const FDI_PRIMARY_UPPER_RIGHT = [55, 54, 53, 52, 51];
export const FDI_PRIMARY_UPPER_LEFT = [61, 62, 63, 64, 65];
export const FDI_PRIMARY_LOWER_LEFT = [71, 72, 73, 74, 75];
export const FDI_PRIMARY_LOWER_RIGHT = [85, 84, 83, 82, 81];

export const ESTADOS_DIENTE = { sano: 'Sano', observacion: 'En observación', problema: 'Problema' };
export const TRATAMIENTOS = ['ninguno', 'limpieza', 'caries', 'extracción', 'obturación', 'endodoncia', 'corona'];

// Superficies por diente: 5 en molares/premolares (oclusal + 4 caras), 4 en incisivos/caninos
export const SUPERFICIES_5 = ['Oclusal', 'Vestibular', 'Lingual', 'Mesial', 'Distal'];
export const SUPERFICIES_4 = ['Incisal', 'Vestibular', 'Lingual', 'Mesial'];

export function getSurfaceLabels(fdiNumber) {
  const pos = fdiNumber % 10;
  const decena = Math.floor(fdiNumber / 10);
  const isPrimary = decena === 5 || decena === 6 || decena === 7 || decena === 8;
  if (isPrimary) return pos === 1 || pos === 2 ? SUPERFICIES_5 : SUPERFICIES_4; // molares temp = 5
  if (pos >= 4 && pos <= 8) return SUPERFICIES_5; // premolares y molares permanentes
  return SUPERFICIES_4; // incisivos y caninos
}

export function getSurfacesCount(fdiNumber) {
  return getSurfaceLabels(fdiNumber).length;
}

// Opciones para marcar por superficie (centro/lado)
export const TRATAMIENTOS_SUPERFICIE = [
  { value: '', label: '—' },
  { value: 'sano', label: 'Sano' },
  { value: 'caries', label: 'Caries' },
  { value: 'obturacion', label: 'Obturación' },
  { value: 'restauracion', label: 'Restauración' },
  { value: 'fractura', label: 'Fractura' },
  { value: 'desgaste', label: 'Desgaste' },
];

// Leyenda odontograma: símbolo, etiqueta, color, tipo de símbolo (azul=tratamiento/condición, rojo=patología/temporal)
export const ODONTO_LEYENDA = [
  { id: 'caries', label: 'Caries', symbolType: 'circle-red', color: '#dc3545', description: 'Círculo rojo relleno' },
  { id: 'obturacion', label: 'Obturación', symbolType: 'square-blue', color: '#002A54', description: 'Cuadrado azul relleno' },
  { id: 'restauracion', label: 'Restauración definitiva', symbolType: 'square-blue', color: '#002A54', description: 'Cuadrado azul relleno' },
  { id: 'restauracion_temp', label: 'Restauración temporal', symbolType: 'square-red-outline', color: '#dc3545', description: 'Cuadrado rojo sin rellenar' },
  { id: 'corona_def', label: 'Corona definitiva', symbolType: 'crown-blue', color: '#002A54', description: 'Corona azul' },
  { id: 'corona_temp', label: 'Corona temporal', symbolType: 'crown-red', color: '#dc3545', description: 'Corona roja' },
  { id: 'endodoncia', label: 'Endodoncia', symbolType: 'root-canal', color: '#002A54', description: 'Línea vertical en raíz' },
  { id: 'extraccion', label: 'Extracción indicada', symbolType: 'extraction', color: '#dc3545', description: 'X roja sobre diente' },
  { id: 'ausente', label: 'Diente ausente', symbolType: 'missing', color: '#64748b', description: 'X gris con línea' },
  { id: 'implante', label: 'Implante', symbolType: 'implant', color: '#002A54', description: 'Tornillo azul' },
  { id: 'protesis_fija', label: 'Prótesis fija', symbolType: 'bridge', color: '#002A54', description: 'Línea continua superior' },
  { id: 'protesis_removible', label: 'Prótesis removible', symbolType: 'removable', color: '#002A54', description: 'Línea discontinua' },
  { id: 'protesis_total', label: 'Prótesis total', symbolType: 'total-prosthesis', color: '#002A54', description: 'Doble línea' },
  { id: 'fractura', label: 'Fractura', symbolType: 'fracture', color: '#dc3545', description: 'Línea diagonal roja' },
  { id: 'sellante', label: 'Sellante', symbolType: 'sealant', color: '#10b981', description: 'S verde' },
];

// --- Radiografías (por paciente) ---
export const getRadiografias = async (idPaciente) => {
  await migrateAllLocalStorageToServer();
  const list = await fetchRecords(RECORD_TYPES.RADIOGRAPH, { patientId: idPaciente });
  return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
};

export const saveRadiografia = async (item) => {
  const r = { ...item };
  if (!r.id) r.id = `rx-${Date.now()}`;
  if (!r.codigoArchivo) r.codigoArchivo = r.id;
  return saveRecordApi(RECORD_TYPES.RADIOGRAPH, r.id, r);
};

export const deleteRadiografia = async (id) => {
  await deleteRecordApi(RECORD_TYPES.RADIOGRAPH, id);
};

export const TIPOS_RADIOGRAFIA = ['Periapical', 'Oclusal', 'Panorámica', 'Bitewing', 'Cefalométrica', 'Otra'];

// --- Recetas médicas (por paciente) ---
export const getRecetas = async (idPaciente) => {
  await migrateAllLocalStorageToServer();
  const list = await fetchRecords(RECORD_TYPES.PRESCRIPTION, { patientId: idPaciente });
  return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
};

export const saveReceta = async (receta, idDoctor) => {
  const r = { ...receta, idDoctor };
  if (!r.id) r.id = `rec-${Date.now()}`;
  return saveRecordApi(RECORD_TYPES.PRESCRIPTION, r.id, r);
};

export const deleteReceta = async (id) => {
  await deleteRecordApi(RECORD_TYPES.PRESCRIPTION, id);
};

// --- Pagos y abonos (por paciente) ---
export const getPagos = async (idPaciente) => {
  await migrateAllLocalStorageToServer();
  const list = await fetchRecords(RECORD_TYPES.PAYMENT, { patientId: idPaciente });
  return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
};

export const savePago = async (pago, idDoctor) => {
  const p = { ...pago, idDoctor };
  if (!p.id) p.id = `pago-${Date.now()}`;
  return saveRecordApi(RECORD_TYPES.PAYMENT, p.id, p);
};

export const TIPOS_MOVIMIENTO = [{ value: 'pago', label: 'Pago' }, { value: 'abono', label: 'Abono' }, { value: 'devolucion', label: 'Devolución' }];

// --- Productos (inventario consultorio) ---
export const getProductosConsultorio = async (idDoctor) => {
  await migrateAllLocalStorageToServer();
  return fetchRecords(RECORD_TYPES.PRODUCT, { doctorIds: [idDoctor] });
};

export const saveProductoConsultorio = async (producto, idDoctor) => {
  const toSave = { ...producto, idDoctor };
  if (!toSave.id) toSave.id = `prod-${Date.now()}`;
  return saveRecordApi(RECORD_TYPES.PRODUCT, toSave.id, toSave);
};

export const deleteProductoConsultorio = async (id) => {
  await deleteRecordApi(RECORD_TYPES.PRODUCT, id);
};

/** Categorías para inventario odontológico */
export const CATEGORIAS_PRODUCTO = [
  'Anestesia',
  'Restauración',
  'Endodoncia',
  'Bioseguridad',
  'Higiene',
  'Impresiones',
  'Esterilización',
  'Instrumental',
  'Material dental',
  'Farmacia',
  'Equipo',
  'Otro'
];

/** Unidades de medida para stock */
export const UNIDADES_MEDIDA = [
  { value: 'und', label: 'Unidad' },
  { value: 'carpule', label: 'Carpule' },
  { value: 'jeringa', label: 'Jeringa' },
  { value: 'caja', label: 'Caja' },
  { value: 'paquete', label: 'Paquete' },
  { value: 'kit', label: 'Kit' },
  { value: 'ml', label: 'ml' },
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'Kg' },
  { value: 'lt', label: 'Litro' },
  { value: 'tubo', label: 'Tubo' },
  { value: 'frasco', label: 'Frasco' }
];

/** Estado del producto en inventario */
export const ESTADOS_PRODUCTO = [
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
  { value: 'descontinuado', label: 'Descontinuado' }
];

/** Valores por defecto para producto/insumo de inventario */
export const PRODUCTO_INVENTARIO_DEFAULTS = {
  codigo: '',
  nombre: '',
  categoria: '',
  subcategoria: '',
  marca: '',
  proveedor: '',
  precioUnitario: 0,
  precioVenta: '',
  unidadMedida: 'und',
  stockActual: 0,
  stockMinimo: 0,
  stockMaximo: '',
  fechaCaducidad: '',
  lote: '',
  ubicacion: '',
  fechaUltimaEntrada: '',
  cantidadUltimaEntrada: '',
  notas: '',
  estado: 'activo',
  movimientos: []
};

/** Registra un movimiento de inventario en el producto y actualiza stock */
export const registrarMovimientoProducto = async (idProducto, tipo, cantidad, nota, idDoctor) => {
  const all = await getProductosConsultorio(idDoctor);
  const prod = all.find((p) => p.id === idProducto && String(p.idDoctor) === String(idDoctor));
  if (!prod) return null;
  const stockAnterior = Number(prod.stockActual ?? prod.stock ?? 0);
  const cant = Number(cantidad) || 0;
  let stockNuevo = stockAnterior;
  if (tipo === 'entrada') stockNuevo = stockAnterior + cant;
  else if (tipo === 'salida') stockNuevo = Math.max(0, stockAnterior - cant);
  else if (tipo === 'ajuste') stockNuevo = cant;
  const mov = {
    fecha: new Date().toISOString(),
    tipo,
    cantidad: cant,
    stockAnterior,
    stockNuevo,
    nota: nota || '',
  };
  const movimientos = Array.isArray(prod.movimientos) ? [...prod.movimientos, mov] : [mov];
  const updated = {
    ...prod,
    stockActual: stockNuevo,
    movimientos,
    fechaUltimaEntrada: tipo === 'entrada' ? mov.fecha : (prod.fechaUltimaEntrada || ''),
    cantidadUltimaEntrada: tipo === 'entrada' ? cant : (prod.cantidadUltimaEntrada ?? ''),
  };
  return saveProductoConsultorio(updated, idDoctor);
};

// --- Facturas consultorio ---
export const getFacturasConsultorio = async (idDoctor) => {
  await migrateAllLocalStorageToServer();
  const list = await fetchRecords(RECORD_TYPES.CLINIC_INVOICE, { doctorIds: [idDoctor] });
  return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
};

export const saveFacturaConsultorio = async (factura, idDoctor) => {
  const f = { ...factura, idDoctor };
  if (!f.id) f.id = `fac-${Date.now()}`;
  return saveRecordApi(RECORD_TYPES.CLINIC_INVOICE, f.id, f);
};

export const ESTADOS_FACTURA = ['pendiente', 'pagada', 'anulada'];

// --- CONSTANTES PARA FORMULARIO MSP BRYMA DENTAL ---

/** ETAPA 1: Campos que el doctor debe llenar en el REGISTRO INICIAL (antes de la cita) */
export const CAMPOS_REGISTRO_INICIAL = [
  'nombre',
  'apellidos',
  'sexo',
  'edad',
  'fechaNacimiento',
  'establecimiento',
  'numeroHistoriaClinica',
  'rangoEdad',
  'embarazada',
  'cedula',
  'tipoIdentificacion',
  'telefono',
  'email',
  'direccion',
  'direccionProvincia',
  'direccionCanton',
  'direccionParroquia',
  'direccionCiudad',
  'direccionExacta',
  // 1. MOTIVO DE CONSULTA
  'motivoConsulta',
  // 2. ENFERMEDAD O PROBLEMA ACTUAL
  'enfermedadActual',
  'sintomas',
  'cronologia',
  'localizacion',
  'caracteristicas',
  'intensidad',
  'causaAparente',
  'sintomasAsociados',
  'evolucion',
  'estadoActual',
  // 3. ANTECEDENTES PERSONALES Y FAMILIARES
  'alergiaAntibiotico',
  'alergiaAnestesia',
  'hemorragias',
  'vih',
  'tuberculosis',
  'asma',
  'diabetes',
  'hipertension',
  'antecedentesCancer',
  'antecedentesCardiopatia',
  'frecuenciaCardiaca',
  'otrosAntecedentes',
  'alergiasTipos',
  'alergiasEspecificacion',
  'medicamentosActualesFilas',
  'enfermedadesCronicasSeleccion',
  'enfermedadesCronicasOtro',
  'habitosSeleccion',
  // 4. SIGNOS VITALES
  'presionArterial',
  'frecuenciaCardiacaSignos',
  'temperatura',
  'saturacionOxigeno',
  'frecuenciaRespiratoria',
  // 5. EXAMEN DEL SISTEMA ESTOMATOGNÁTICO
  'examenLabios',
  'examenMejillas',
  'examenMaxilarSuperior',
  'examenMaxilarInferior',
  'examenLengua',
  'examenPaladar',
  'examenPiso',
  'examenCarrillos',
  'examenGlandulasSalivales',
  'examenOrofaringe',
  'examenATM',
  'examenGanglios',
  'antecedentesOdontoExtrasValores',
  'antecedentesOdontoDetalles',
  'signosVitalesExtrasValores',
  'examenEstomatognaticoMarcas',
  'examenEstomatognaticoDetalles',
  'examenOdontoExtrasValores',
  'examenOdontoExtrasMarcas',
  'consentimientoInformadoTexto',
  'consentimientoInformadoFirmaDataUrl',
  'consentimientoInformadoFirmaStorageId',
  'consentimientoInformadoFirmaModo',
];

/** ETAPA 2: Campos que se llenan DURANTE LA CITA MÉDICA */
export const CAMPOS_DURANTE_CITA = [
  // 6. ODONTOGRAMA (se maneja en sistema separado de dental charts)
];

/** ETAPA 3: Campos que se llenan POST-CITA (el médico da indicaciones) */
export const CAMPOS_POST_CITA = [
  // 7. INDICADORES DE SALUD BUCAL
  'higieneOralSimplificada',
  'placa',
  'calculo',
  'gingivitis',
  'enfermedadPeriodontal',
  'maloclusion',
  'angleClasificacion',
  'fluorosis',
  // 8. ÍNDICE CPO-ceo
  'indiceCPO_D',
  'indiceCPO_C',
  'indiceCPO_P',
  'indiceCPO_O',
  'indiceCPO_Total',
  'indiceCeo_d',
  'indiceCeo_c',
  'indiceCeo_e',
  'indiceCeo_o',
  'indiceCeo_Total',
  // 10. PLANES DE DIAGNÓSTICO
  'biometria',
  'quimicaSanguinea',
  'rayosX',
  'otrosExamenes',
  // 11. DIAGNÓSTICO
  'diagnosticoPresuntivo1',
  'diagnosticoPresuntivoCIE10_1',
  'diagnosticoDefinitivo1',
  'diagnosticoDefinitivoCIE10_1',
  'diagnosticoPresuntivo2',
  'diagnosticoPresuntivoCIE10_2',
  'diagnosticoDefinitivo2',
  'diagnosticoDefinitivoCIE10_2',
  'diagnosticoPresuntivo3',
  'diagnosticoPresuntivoCIE10_3',
  'diagnosticoDefinitivo3',
  'diagnosticoDefinitivoCIE10_3',
  'diagnosticoPresuntivo4',
  'diagnosticoPresuntivoCIE10_4',
  'diagnosticoDefinitivo4',
  'diagnosticoDefinitivoCIE10_4',
  'fechaApertura',
  'fechaControl',
  // Tablas MSP diagnóstico / tratamiento (ficha odontológica)
  'tablaDiagnosticosMSP',
  'tablaTratamientosMSP',
  // 12. TRATAMIENTO (sesiones adicionales en sistema separado)
];

/** Rangos de edad para clasificación en historia clínica */
export const RANGOS_EDAD = [
  { value: 'menor_1', label: 'Menores de 1 año' },
  { value: '1_4', label: '1 - 4 años' },
  { value: '5_9', label: '5 - 9 años' },
  { value: '10_14', label: '10 - 14 años' },
  { value: '15_19', label: '15 - 19 años' },
  { value: 'mayor_20', label: 'Mayor de 20 años' }
];

/** Opciones para antecedentes (Sí/No) */
export const OPCIONES_SI_NO = [
  { value: '', label: '—' },
  { value: 'si', label: 'Sí' },
  { value: 'no', label: 'No' }
];

/** Niveles de higiene oral - Placa bacteriana */
export const NIVELES_PLACA = [
  { value: '0', label: '0 - Sin placa' },
  { value: '1', label: '1 - Placa leve' },
  { value: '2', label: '2 - Placa moderada' },
  { value: '3', label: '3 - Placa severa' }
];

/** Niveles de higiene oral - Cálculo */
export const NIVELES_CALCULO = [
  { value: '0', label: '0 - Sin cálculo' },
  { value: '1', label: '1 - Cálculo leve' },
  { value: '2', label: '2 - Cálculo moderado' },
  { value: '3', label: '3 - Cálculo severo' }
];

/** Niveles de gingivitis */
export const NIVELES_GINGIVITIS = [
  { value: '0', label: '0 - Sin gingivitis' },
  { value: '1', label: '1 - Con gingivitis' }
];

/** Clasificación de enfermedad periodontal */
export const NIVELES_ENFERMEDAD_PERIODONTAL = [
  { value: 'sin_enfermedad', label: 'Sin enfermedad' },
  { value: 'leve', label: 'Leve' },
  { value: 'moderada', label: 'Moderada' },
  { value: 'severa', label: 'Severa' }
];

/** Clasificación de Angle para maloclusión */
export const CLASIFICACION_ANGLE = [
  { value: 'sin_maloclusion', label: 'Sin maloclusión' },
  { value: 'angle_i', label: 'Angle Clase I' },
  { value: 'angle_ii', label: 'Angle Clase II' },
  { value: 'angle_iii', label: 'Angle Clase III' }
];

/** Niveles de fluorosis */
export const NIVELES_FLUOROSIS = [
  { value: 'sin_fluorosis', label: 'Sin fluorosis' },
  { value: 'leve', label: 'Leve' },
  { value: 'moderada', label: 'Moderada' },
  { value: 'severa', label: 'Severa' }
];

/** Estados del examen estomatognático */
export const ESTADOS_EXAMEN = [
  { value: 'normal', label: 'Normal' },
  { value: 'alterado', label: 'Alterado' }
];

// --- ESTRUCTURA PARA SESIONES DE TRATAMIENTO (ETAPA 3) ---

/** Estructura por defecto para una sesión de tratamiento */
export const SESION_TRATAMIENTO_DEFAULTS = {
  numeroSesion: 1,
  fecha: '',
  diagnosticoComplicaciones: '',
  procedimientos: '',
  prescripciones: '',
  codigo: '',
  firma: ''
};

/** Obtiene todas las sesiones de tratamiento de un paciente */
export const getSesionesTratamiento = async (idPaciente) => {
  await migrateAllLocalStorageToServer();
  const list = await fetchRecords(RECORD_TYPES.TREATMENT_SESSION, { patientId: idPaciente });
  return list.sort((a, b) => (a.numeroSesion || 0) - (b.numeroSesion || 0));
};

/** Guarda una sesión de tratamiento */
export const saveSesionTratamiento = async (sesion, idPaciente, idDoctor) => {
  const s = { ...sesion, idPaciente, idDoctor };
  if (!s.id) s.id = `sesion-${Date.now()}`;
  return saveRecordApi(RECORD_TYPES.TREATMENT_SESSION, s.id, s);
};

/** Elimina una sesión de tratamiento */
export const deleteSesionTratamiento = async (id) => {
  await deleteRecordApi(RECORD_TYPES.TREATMENT_SESSION, id);
};

/** Preferencias de UI (acordeones abiertos, etc.) */
const uiPrefMemory = new Map();
const uiPrefPending = new Map();
let uiPrefFlushTimer = null;
const UI_PREF_FLUSH_MS = 400;

function scheduleUiPrefFlush() {
  if (uiPrefFlushTimer) clearTimeout(uiPrefFlushTimer);
  uiPrefFlushTimer = setTimeout(() => {
    uiPrefFlushTimer = null;
    const batch = new Map(uiPrefPending);
    uiPrefPending.clear();
    void Promise.all(
      [...batch.entries()].map(([key, value]) =>
        saveRecordApi(RECORD_TYPES.UI_PREF, key, { key, value }),
      ),
    );
  }, UI_PREF_FLUSH_MS);
}

export const getUiPref = async (key) => {
  if (uiPrefMemory.has(key)) return uiPrefMemory.get(key);
  await migrateAllLocalStorageToServer();
  const rows = await fetchRecords(RECORD_TYPES.UI_PREF);
  const row = rows.find((r) => r.key === key || r.id === key);
  const value = row?.value ?? null;
  if (value != null) uiPrefMemory.set(key, value);
  return value;
};

export const saveUiPref = (key, value) => {
  const strVal = String(value);
  if (uiPrefMemory.get(key) === strVal) return Promise.resolve();
  uiPrefMemory.set(key, strVal);
  uiPrefPending.set(key, strVal);
  scheduleUiPrefFlush();
  return Promise.resolve();
};

/** Periodontograma por paciente */
export const getPeriodontograma = async (idPaciente) => {
  await migrateAllLocalStorageToServer();
  const ext = `perio-${idPaciente}`;
  const rows = await fetchRecords(RECORD_TYPES.PERIODONTOGRAM, { patientId: idPaciente });
  const row = rows.find((r) => r.id === ext) || rows[0];
  return row?.datos || row || {};
};

export const savePeriodontograma = async (idPaciente, datos, idDoctor) => {
  const ext = `perio-${idPaciente}`;
  return saveRecordApi(RECORD_TYPES.PERIODONTOGRAM, ext, {
    idPaciente,
    idDoctor,
    datos,
  });
};
